import { Injectable } from '@angular/core';
import { SupabaseSelectQueryConfig } from '@examdojo/core/supabase';
import {
  QuestionImageHttpModel,
  QuestionVoteStoreModel,
  StemDifficultyCategory,
  StemHttpModel,
  StimulusHttpModel,
} from '@examdojo/models/question';
import { DbJson, ExamdojoSupabaseService, SupabaseBaseHttpService } from '@examdojo/supabase';
import { from, map, Observable } from 'rxjs';
import { DerivativeQuestionItemsViewHttpModel, QuestionEntityStoreModel } from './models';
import { Feedback } from './question-user-feedback';

@Injectable({
  providedIn: 'root',
})
export class QuestionEntityHttpService extends SupabaseBaseHttpService<'questions'> {
  constructor(supabase: ExamdojoSupabaseService) {
    super(supabase);
  }

  readonly schema = 'public';
  readonly tableName = 'questions';
  readonly viewName = 'derivative_questions';

  fetchItems(questionId: QuestionEntityStoreModel['id']) {
    return from(
      this.supabase.client
        .rpc('get_question_items', {
          p_question_id: questionId,
        })
        .throwOnError()
        .then(
          (res) =>
            (res.data ?? []) as Array<
              (
                | ({ item_type: 'stimulus' } & Omit<StimulusHttpModel, 'id'>)
                | ({ item_type: 'stem' } & Omit<StemHttpModel, 'id'>)
                | ({ item_type: 'question_image' } & Omit<QuestionImageHttpModel, 'id'>)
              ) & { item_id: number }
            >,
        ),
    );
  }

  fetchSignedUrls(imageNames: string[], bucketName: string) {
    return this.supabase.getSignedUrlsForFiles(bucketName, imageNames);
  }

  fetchPaginatedListView(
    selectQueryConfig: SupabaseSelectQueryConfig = {},
  ): Observable<{ data: DerivativeQuestionItemsViewHttpModel[]; totalCount: number }> {
    const query = this.supabase.createSelectQuery('public', 'derivative_question_items_view', selectQueryConfig);

    return from(query).pipe(
      map(({ data, count }) => ({
        data: data!,
        totalCount: count!,
      })),
    ) as Observable<{ data: DerivativeQuestionItemsViewHttpModel[]; totalCount: number }>;
  }

  setQuestionVote(
    id: QuestionEntityStoreModel['id'],
    options: { vote: boolean; problems?: Feedback[] | string[]; textFeedback?: string },
  ): Observable<undefined | null> {
    return from(
      this.supabase.client
        .rpc('set_question_vote', {
          q_id: id,
          user_vote: options.vote,
          user_text_feedback: options.textFeedback || '',
          user_problems: (options.problems as DbJson[]) ?? null,
        })
        .throwOnError()
        .then((res) => res.data),
    );
  }

  removeQuestionVote(voteId: QuestionVoteStoreModel['id']): Observable<void> {
    return from(
      this.supabase.client
        .from('question_votes')
        .delete()
        .eq('id', voteId)
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  fetchQuestionVote(id: QuestionEntityStoreModel['id']) {
    return from(
      this.supabase.client
        .from('question_votes')
        .select()
        .eq('question_id', id)
        .limit(1)
        .maybeSingle()
        .throwOnError()
        .then((d) => d.data),
    );
  }

  fetchSyllabusIdByQuestionId(id: QuestionEntityStoreModel['id']): Observable<number | null> {
    return from(
      this.supabase.client
        .rpc('get_syllabus_id_for_question', { question_id: id })
        .throwOnError()
        .then((response) => response.data),
    );
  }

  fetchQuestionStatus(id: QuestionEntityStoreModel['id']): Observable<string | null> {
    return from(
      this.supabase.client
        .from(this.viewName)
        .select('status')
        .eq('id', id)
        .single()
        .throwOnError()
        .then((response) => response.data?.status as string | null),
    );
  }

  fetchEditHistory(questionId: QuestionEntityStoreModel['id']) {
    return from(
      this.supabase.client
        .from('questions_history')
        .select('*')
        .eq('id', questionId)
        .order('created_at', { ascending: false })
        .throwOnError(),
    ).pipe(map((response) => response.data || []));
  }

  fetchStatusSnapshots(questionId: QuestionEntityStoreModel['id']) {
    return from(
      this.supabase.client
        .from('question_snapshots')
        .select('*')
        .eq('id', questionId)
        .order('snapshot_id', { ascending: false })
        .throwOnError(),
    ).pipe(map((response) => response.data));
  }

  override fetch(itemId: number | string) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.viewName)
        .select()

        .eq(this.id_field, itemId)
        .single()
        .throwOnError()
        .then((response) => response.data!),
    ) as ReturnType<SupabaseBaseHttpService<'questions'>['fetch']> & {
      difficulty?: StemDifficultyCategory | null;
    };
  }

  fetchByIds(itemIds: number[] | string[]) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.viewName)
        .select()

        .in(this.id_field, itemIds)

        .throwOnError()
        .then((response) => response.data!),
    );
  }
  override fetchAll() {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.viewName)
        .select()
        .throwOnError()
        .then((response) => response.data!),
    ) as ReturnType<SupabaseBaseHttpService<'questions'>['fetchAll']> & {
      difficulty?: StemDifficultyCategory | null;
    };
  }

  override createSelectQuery(selectQueryConfig: SupabaseSelectQueryConfig = {}) {
    const query = this.supabase.createSelectQuery(this.schema, this.viewName, selectQueryConfig);

    return from(query).pipe(
      map(({ data, count }) => ({
        data: data!,
        totalCount: count!,
      })),
    );
  }

  createSeedQuestion(sourceQuestionId: QuestionEntityStoreModel['id']): Observable<number | null> {
    return from(
      this.supabase.client
        .rpc('create_derivative_seed_question_from_derivative_question', {
          derivative_question_id: sourceQuestionId,
        })
        .throwOnError()
        .then((response) => response.data),
    );
  }
}
